import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";

import type { NevoraDataProvider } from "../datos/dataProvider";
import type { Negocio } from "../dominio/tipos";

/**
 * S4 informativo (S9, decisión 5 del 02/10/2026): asignar un negocio con una campaña viva en
 * Nevora Prospect ya no da error; la ficha lo avisa para que Jan y Robert lo sepan. Solo
 * para administradores (el esquema de Prospect solo lo leen ellos).
 */
export const AvisoCampanaProspect = ({ negocio }: { negocio: Negocio }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const { data: viva } = useQuery({
    queryKey: ["nevora", "inscripcionViva", negocio.negocio_id],
    queryFn: () => dataProvider.inscripcionViva(negocio.negocio_id),
    enabled: negocio.origen === "prospect",
    staleTime: 60_000,
  });
  if (!viva) return null;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
      <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
        {translate("nevora.ficha.campana_prospect")}
      </p>
      <BotonSacarDeCampana negocio={negocio} />
    </div>
  );
};

/**
 * «Sacar de la campaña de email» (32, Robert 15–16/10/2026): solo Jan y Robert (el aviso ya
 * solo lo ven ellos). Sin la 32 en la base no sale.
 */
const BotonSacarDeCampana = ({ negocio }: { negocio: Negocio }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const [sacando, setSacando] = useState(false);
  const { data: disponible } = useQuery({
    queryKey: ["nevora", "sacarDeCampanaDisponible"],
    queryFn: () => dataProvider.sacarDeCampanaDisponible(),
    staleTime: Infinity,
    retry: false,
  });
  if (!disponible) return null;
  const sacar = async () => {
    if (!window.confirm(translate("nevora.ficha.sacar_de_campana_confirmar")))
      return;
    setSacando(true);
    try {
      await dataProvider.sacarDeCampana(negocio.negocio_id);
      notify("nevora.ficha.sacar_de_campana_hecho", { type: "success" });
      await queryClient.invalidateQueries({
        queryKey: ["nevora", "inscripcionViva", negocio.negocio_id],
      });
    } catch {
      notify("nevora.ficha.sacar_de_campana_error", { type: "error" });
    } finally {
      setSacando(false);
    }
  };
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={sacar}
      disabled={sacando}
      data-testid="sacar-de-campana"
    >
      {translate("nevora.ficha.sacar_de_campana")}
    </Button>
  );
};
